// Sentry MUST be the first thing initialised so client-side boot errors are
// captured even before React mounts. Guarded on VITE_SENTRY_DSN — if no DSN is
// configured (dev machines without one, preview builds, etc.) we skip init and
// log a single warning so the console isn't spammed.
//
// The init logic itself lives in `src/lib/sentry.ts` as a test seam — see
// `src/tests/sentryClient.test.ts`.
import * as Sentry from '@sentry/react'
import { initSentry } from './lib/sentry'

initSentry({
  dsn: import.meta.env.VITE_SENTRY_DSN as string | undefined,
  mode: import.meta.env.MODE,
  release: import.meta.env.VITE_RELEASE as string | undefined,
});

import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { store } from './store'
import App from './App.tsx'
import './index.css'
import { logVersion } from './lib/version'
import { captureLandingAttribution } from './lib/analytics'

// Log version information when the app starts
logVersion();

// COP-11 — capture UTM params on the very first paint so we never miss a landing-with-utms
// even if the user closes the tab before mounting any route.
void captureLandingAttribution();

// Minimal Spanish-language fallback used by Sentry.ErrorBoundary when an
// uncaught React error escapes the tree. Kept intentionally simple so we don't
// drag in additional design tokens.
const SentryFallback = (
  <div
    role="alert"
    style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      color: '#111827',
      backgroundColor: '#f9fafb',
      textAlign: 'center',
    }}
  >
    <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '8px' }}>
      Algo salió mal
    </h1>
    <p style={{ color: '#4b5563' }}>
      Recarga la página para continuar.
    </p>
  </div>
);

// Ensure Redux Provider wraps the entire application
createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary fallback={SentryFallback}>
    <Provider store={store}>
      <App />
    </Provider>
  </Sentry.ErrorBoundary>
);
